import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartConfiguration,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface Props {
  data: {
    type: "line_chart";
    title?: string;
    labels: string[];
    values: number[];
    unit?: string;
    color?: string;
    animation?: string;
  };
}

export function ElementLineChart({ data }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.labels?.length) return;
    const color = data.color || "#6366f1";
    const config: ChartConfiguration<"line"> = {
      type: "line",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: data.unit ?? "Value",
            data: data.values,
            borderColor: color,
            backgroundColor: color + "33",
            fill: true,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: !!data.title, text: data.title ?? "" },
          legend: { display: false },
        },
        scales: {
          y: { beginAtZero: true },
        },
      },
    };
    const chart = new ChartJS(canvasRef.current, config);
    return () => chart.destroy();
  }, [data]);

  return (
    <motion.div
      className="h-48 w-full"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <canvas ref={canvasRef} />
    </motion.div>
  );
}
